import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import "../../newstyle.css";
import SearchBox from "../../../../Components/SearchBox";
import { Formik } from "formik";
import { useQuery } from "@tanstack/react-query";
import {
  GetAdminUserListing,
  updateAdminStatus,
} from "../../../../Api/adminUser";
import {
  ErrorToast,
  SuccessToast,
  Confirmation
} from "../../../../Utils/SweetAlert";
import { useMutateDeleteUser } from "../../hooks/useApiHooks";
import Pagination from "../../../../Utils";
import NameInitial from "../../../../Components/NameInitial/Nameinitial";
import AddButton from "../../../../Components/AddButton";
import { EditButton, DeleteButton, StatusButton } from "../../../../Components";

const AdminUserListing = () => {
  const formRef = useRef();

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const deleteUser = useMutateDeleteUser();

  const {
    isFetching: userDataLoading,
    data: userData,
    refetch,
  } = useQuery({
    queryKey: ["get-user-list", limit, page],
    queryFn: () =>
      GetAdminUserListing({
        limit: limit,
        page: page === 0 ? 1 : page + 1,
        search: formRef?.current?.values?.search,
      }),
    throwOnError: (error) => {
      ErrorToast(error?.response?.data?.message || "Server Error.");
    },
  });

  const handlePageChange = async (e, pageNew) => {
    setPage(pageNew);
    refetch();
  };

  const handleLimitChange = async (event) => {
    setPage(0);
    setLimit(parseInt(event.target.value, 10));
    refetch();
  };

  return (
    <>
      <Helmet>
        <title>OVE | Admin User</title>
      </Helmet>
      <div className="page-wrapper admin-user">
        <div className="content container-fluid">
          <div className="admin-wrapper">
            <div className="page-header">
              <div className="col">
                <h1 className="page-title">Admin User</h1>
              </div>
              <div className="col-12 col-md-3 col-lg-2 px-2">
                <Formik initialValues={{ search: '' }} innerRef={formRef}>
                  {() => (
                    <form>
                      <SearchBox refetch={refetch} setPage={setPage} />
                    </form>
                  )}
                </Formik>
              </div>
              <div>
                <AddButton
                  to="/admin/add-admin-user"
                  permission="create-admin-user"
                />
              </div>
            </div>
            <div className="col-sm-12 table-width">
              <div className="cards-table card-table">
                <div className="card-body">
                  <div className="table-responsive admin-user-table custom-pagination table-no-padding">
                    <table className="table datatable table-manager mb-0">
                      <thead className="thead-light">
                        <tr>
                          <th className="col-image">Image</th>
                          <th className="col-name">Name</th>
                          <th className="col-email">Email</th>
                          <th className="col-mobile">Mobile</th>
                          <th className="col-role">Role</th>
                          <th className="col-status">Status</th>
                          <th className="col-action">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userDataLoading && (
                          <tr>
                            <td align="center" colSpan={7}>
                              <div className="d-flex justify-content-center align-items-center py-4">
                                <div className="spinner-border loader-theme" role="status" />
                              </div>
                            </td>
                          </tr>
                        )}
                        {!userDataLoading && userData !== undefined && (
                          <>
                            {userData?.data?.data.map((item, index) => {
                              return (
                                <tr
                                  key={index}
                                  className={
                                    item?.role === "superadmin"
                                      ? "table disable-full-row"
                                      : "table"
                                  }
                                >
                                  <td className="col-image">
                                    {item.profilePicUrl ? (
                                      <img
                                        src={item?.profilePicUrl}
                                        alt="img"
                                        className="list-image-news"
                                      />
                                    ) : (
                                      <NameInitial
                                        name={item.name}
                                        type="image"
                                      />
                                    )}
                                  </td>
                                  <td className="col-name">
                                    <span className="user-cell-ellipsis">{item?.name || '-'}</span>
                                  </td>
                                  <td className="col-email">
                                    <span className="user-cell-ellipsis">{item?.email || '-'}</span>
                                  </td>
                                  <td className="col-mobile">
                                    <span className="user-cell-ellipsis">{item?.mobileNo ? `${item?.countryCode} ${item?.mobileNo}` : '-'}</span>
                                  </td>
                                  <td className="col-role">
                                    <span className="user-cell-ellipsis">{item?.roleId?.name || item?.role || '-'}</span>
                                  </td>
                                  <td className="col-status">
                                    <StatusButton
                                      status={item?.status}
                                      onStatusChange={(status) => {
                                        Confirmation(
                                          status === true
                                            ? "Are you sure you want to active user status?"
                                            : "Are you sure you want to inactive user status?"
                                        ).then((result) => {
                                          if (result.isConfirmed) {
                                            (async () => {
                                              try {
                                                const res = await updateAdminStatus({ id: item?._id });
                                                if (res?.status === 1) {
                                                  SuccessToast(res?.message || 'Status Updated');
                                                  refetch();
                                                } else {
                                                  ErrorToast(res?.message || 'Server Error.');
                                                }
                                              } catch (error) {
                                                ErrorToast(error?.response?.data?.message || 'Server Error.');
                                              }
                                            })();
                                          }
                                        });
                                      }}
                                      disabled={item?.role === "superadmin"}
                                      editPermission="edit-admin-user"
                                    />
                                  </td>
                                  <td className="user-td-padding-remove col-action">
                                    <EditButton
                                      to="/admin/edit-admin-user"
                                      state={{ id: item?._id }}
                                      permission="edit-admin-user"
                                      disabled={item?.role === "superadmin"}
                                    />
                                    <DeleteButton
                                      onDelete={() => {
                                        Confirmation("Are you sure you want to delete user?").then((result) => {
                                          if (result.isConfirmed) {
                                            const reqData = { id: item?._id };
                                            deleteUser.mutate(reqData, {
                                              onSuccess: (response) => {
                                                if (response?.status != 0) {
                                                  SuccessToast(response?.message || "Delete Successful.");
                                                  refetch();
                                                } else {
                                                  ErrorToast(response?.message || "Server Error.");
                                                }
                                              },
                                              onError: (error) => {
                                                ErrorToast(error?.response?.data?.message || "Server Error.");
                                              },
                                            });
                                          }
                                        });
                                      }}
                                      permission="delete-admin-user"
                                      disabled={item?.role === "superadmin"}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                            {userData?.data?.data?.length === 0 && (
                              <tr>
                                <td className="text-center" colSpan={7}>
                                  No Records Found.
                                </td>
                              </tr>
                            )}
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <Pagination
              totalItem={userData?.data?.totalRecords || 0}
              itemsPerPage={limit}
              page={
                !userData?.data?.totalRecords ||
                  userData?.data?.totalRecords <= 0
                  ? 0
                  : page
              }
              handleChangePage={handlePageChange}
              handleChangeRowsPerPage={handleLimitChange}
              colSpan={7}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminUserListing;
